import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiPlus, FiEdit2, FiTrash2, FiDownload, FiFileText, FiEye, FiUpload, FiCopy, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import Layout from '../components/Layout/Layout';
import api from '../services/api';

const Container = styled.div`
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  margin: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &.primary {
    background: ${props => props.theme?.colors?.primary || '#6366f1'};
    color: white;
    
    &:hover {
      background: ${props => props.theme?.colors?.primaryDark || '#4f46e5'};
    }
  }
  
  &.secondary {
    background: ${props => props.theme?.colors?.backgroundAlt || '#f3f4f6'};
    color: ${props => props.theme?.colors?.text || '#1a1a2e'};
    
    &:hover {
      background: ${props => props.theme?.colors?.border || '#e5e7eb'};
    }
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
  border-radius: 6px;
  background: ${props => props.theme?.colors?.background || '#ffffff'};
  color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  font-size: 14px;
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
  border-radius: 6px;
  background: ${props => props.theme?.colors?.background || '#ffffff'};
  color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  font-size: 14px;
  width: 250px;
  
  &::placeholder {
    color: ${props => props.theme?.colors?.textSecondary || '#888'};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
`;

const Card = styled.div`
  background: ${props => props.theme?.colors?.surface || '#ffffff'};
  border-radius: 12px;
  border: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
  overflow: hidden;
  transition: all 0.2s;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme?.colors?.text || '#1a1a2e'};
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CategoryBadge = styled.span`
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: 500;
  text-transform: uppercase;
  
  &.sales { background: #dcfce7; color: #166534; }
  &.service { background: #dbeafe; color: #1e40af; }
  &.nda { background: #fef3c7; color: #92400e; }
  &.partnership { background: #f3e8ff; color: #6b21a8; }
  &.employment { background: #fce7f3; color: #9d174d; }
  &.consulting { background: #cffafe; color: #0e7490; }
  &.subscription { background: #e0e7ff; color: #3730a3; }
  &.other { background: #f3f4f6; color: #374151; }
`;

const CardDescription = styled.p`
  font-size: 14px;
  color: ${props => props.theme?.colors?.textSecondary || '#666'};
  margin: 0;
  line-height: 1.5;
`;

const CardBody = styled.div`
  padding: 20px;
`;

const VariablesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const VariableTag = styled.span`
  font-size: 12px;
  padding: 4px 8px;
  background: ${props => props.theme?.colors?.backgroundAlt || '#f3f4f6'};
  color: ${props => props.theme?.colors?.text || '#374151'};
  border-radius: 4px;
  font-family: monospace;
`;

const CardMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: ${props => props.theme?.colors?.textSecondary || '#888'};
`;

const StatusBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  
  &.active { color: #16a34a; }
  &.inactive { color: #dc2626; }
`;

const CardActions = styled.div`
  padding: 16px 20px;
  border-top: 1px solid ${props => props.theme?.colors?.border || '#e0e0e0'};
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.theme?.colors?.backgroundAlt || '#f3f4f6'};
  color: ${props => props.theme?.colors?.text || '#374151'};
  
  &:hover {
    background: ${props => props.theme?.colors?.border || '#e5e7eb'};
  }
  
  &.danger:hover {
    background: #fee2e2;
    color: #dc2626;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: ${props => props.theme?.colors?.surface || '#ffffff'};
  border-radius: 12px;
  border: 2px dashed ${props => props.theme?.colors?.border || '#e0e0e0'};
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background: ${props => props.theme?.colors?.backgroundAlt || '#f3f4f6'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: ${props => props.theme?.colors?.textSecondary || '#888'};
`;

const CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'sales', label: 'Sales Agreement' },
  { value: 'service', label: 'Service Contract' },
  { value: 'nda', label: 'Non-Disclosure Agreement' },
  { value: 'partnership', label: 'Partnership Agreement' },
  { value: 'employment', label: 'Employment Contract' },
  { value: 'consulting', label: 'Consulting Agreement' },
  { value: 'subscription', label: 'Subscription Agreement' },
  { value: 'other', label: 'Other' },
];

export default function ContractTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [showActive, setShowActive] = useState('all');

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (showActive !== 'all') params.append('is_active', showActive === 'active');
      
      const response = await api.get(`/contracts/templates?${params}`);
      setTemplates(response.data.templates || []);
    } catch (err) {
      setError('Failed to load templates');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [category, showActive]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await api.delete(`/contracts/templates/${id}`);
      setTemplates(templates.filter(t => t.id !== id));
    } catch (err) {
      alert('Failed to delete template');
      console.error(err);
    }
  };

  const handleDuplicate = async (template) => {
    try {
      const response = await api.post('/contracts/templates', {
        name: `${template.name} (Copy)`,
        description: template.description,
        category: template.category,
        language: template.language,
        source_template_id: template.id,
      });
      setTemplates([response.data.template, ...templates]);
    } catch (err) {
      alert('Failed to duplicate template');
      console.error(err);
    }
  };

  const filteredTemplates = templates.filter(t => 
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    (t.description && t.description.toLowerCase().includes(search.toLowerCase()))
  );

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const kb = bytes / 1024;
    return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb.toFixed(1)} KB`;
  };

  return (
    <Layout>
      <Container>
        <Header>
          <Title>Contract Templates</Title>
          <ButtonGroup>
            <Button className="primary" onClick={() => navigate('/contracts/templates/new')}>
              <FiPlus /> New Template
            </Button>
          </ButtonGroup>
        </Header>

        <FilterBar>
          <SearchInput 
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select value={category} onChange={(e) => setCategory(e.target.value)}>
            {CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </Select>
          <Select value={showActive} onChange={(e) => setShowActive(e.target.value)}>
            <option value="all">All Status</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </Select>
        </FilterBar>

        {loading ? (
          <p>Loading templates...</p>
        ) : error ? (
          <p style={{ color: 'red' }}>{error}</p>
        ) : filteredTemplates.length === 0 ? (
          <EmptyState>
            <EmptyIcon><FiFileText /></EmptyIcon>
            <h3>No Templates Found</h3>
            <p>Create your first contract template to start generating contracts.</p>
            <Button className="primary" onClick={() => navigate('/contracts/templates/new')} style={{ marginTop: 20 }}>
              <FiPlus /> Create Template
            </Button>
          </EmptyState>
        ) : (
          <Grid>
            {filteredTemplates.map(template => (
              <Card key={template.id}>
                <CardHeader>
                  <CardTitle>
                    <FiFileText />
                    {template.name}
                  </CardTitle>
                  {template.category && (
                    <CategoryBadge className={template.category}>
                      {template.category}
                    </CategoryBadge>
                  )}
                  <CardDescription>{template.description || 'No description'}</CardDescription>
                </CardHeader>
                <CardBody>
                  {template.variables && template.variables.length > 0 && (
                    <VariablesList>
                      {template.variables.slice(0, 5).map((v, i) => (
                        <VariableTag key={i}>{'{{' + v.name + '}}'}</VariableTag>
                      ))}
                      {template.variables.length > 5 && (
                        <VariableTag>+{template.variables.length - 5} more</VariableTag>
                      )}
                    </VariablesList>
                  )}
                  <CardMeta>
                    <span>{formatFileSize(template.file_size)}</span>
                    <StatusBadge className={template.is_active ? 'active' : 'inactive'}>
                      {template.is_active ? <FiCheckCircle /> : <FiXCircle />}
                      {template.is_active ? 'Active' : 'Inactive'}
                    </StatusBadge>
                  </CardMeta>
                </CardBody>
                <CardActions>
                  <ActionButton onClick={() => navigate(`/contracts/templates/${template.id}`)}>
                    <FiEye /> View
                  </ActionButton>
                  <ActionButton onClick={() => navigate(`/contracts/templates/${template.id}/edit`)}>
                    <FiEdit2 /> Edit
                  </ActionButton>
                  <ActionButton onClick={() => handleDuplicate(template)}>
                    <FiCopy /> Duplicate
                  </ActionButton>
                  <ActionButton className="danger" onClick={() => handleDelete(template.id)}>
                    <FiTrash2 />
                  </ActionButton>
                </CardActions>
              </Card>
            ))}
          </Grid>
        )}
      </Container>
    </Layout>
  );
}
